import React, { useState, useEffect, useRef } from 'react';
import { X, Download } from 'lucide-react';
import { faMessage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomModal from "../../components/CustomModal";
import { fetchChatHistory, fetchMessage, fetchUsers, deleteChat, exportChat } from '../../services/api/chatHistory';
import { getSafeImageSrc } from '../../utils/imageUtils';
import ChatHistoryFilters from './ChatHistoryFilters';
import ChatHistoryTable from './ChatHistoryTable';
import ChatHistoryPagination from './ChatHistoryPagination';
import AISummary from './AISummary';

interface ChatHistoryProps {
  isChatHistoryModalOpen: boolean;
  setIsChatHistoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  assistant: any;
}

interface ChatThread {
  id: string;
  user_name: string;
  title: string;
  last_updated: string;
  messages: ChatMessage[];
  isExpanded: boolean;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  content: string;
  sender: string;
  app_id: string;
}

interface User {
  user_id: string;
  user_name: string;
}

interface ChatHistoryRequest {
  app_id: string;
  user_id: string;
  date_range: {
    start_date: string | null;
    end_date: string | null;
  };
  topic_search: string;
  pagination: {
    pageSize: number;
    skip: number;
    searchTerm: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
  isChatHistoryModalOpen,
  setIsChatHistoryModalOpen,
  assistant,
}) => {
  // Date state - Updated to default to last 7 days
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date | null, Date | null]>([sevenDaysAgo, today]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Filter states
  const [selectedSortBy, setSelectedSortBy] = useState('last_updated');
  const [selectedSortOrder, setSelectedSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState("");
  const [topicSearchTerm, setTopicSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);

  // Chat data states
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(assistant?.app_ai_face);

  // Pagination states
  const [pagination, setPagination] = useState({
    pageSize: 10,
    skip: 0,
    searchTerm: '',
    sortBy: 'last_updated',
    sortOrder: 'desc' as 'asc' | 'desc',
  });
  const [totalThreads, setTotalThreads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalThreads / pagination.pageSize);

  // Refs for API debouncing and preventing multiple calls
  const apiCallTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedUser = useRef(false);

  // Custom modal state
  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  // Update safe AI icon source
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = assistant?.app_ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [assistant?.app_ai_face]);

  // Sync selectedSortBy and selectedSortOrder with pagination
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      sortBy: selectedSortBy,
      sortOrder: selectedSortOrder,
      skip: 0, // Reset to first page when sort changes
    }));
    setCurrentPage(1); // Reset to first page when sort changes
  }, [selectedSortBy, selectedSortOrder]);

  // Fetch chat history
  const fetchHistory = async (request: ChatHistoryRequest) => {
    setIsLoading(true);
    try {
      const response = await fetchChatHistory(request);
      if (response) {
        setChatThreads(response.threads);
        setTotalThreads(response.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch messages for a specific thread
  const getMessages = async (threadId: string) => {
    try {
      const thread = chatThreads.find((t) => t.id === threadId);
      // Prevent multiple calls for the same thread
      if ((thread?.messages && thread.messages.length > 0) || loadingThreadId === threadId) return;

      setLoadingThreadId(threadId);
      const response = await fetchMessage(assistant?.app_id, threadId);
      if (response) {
        setChatThreads((prevThreads) =>
          prevThreads?.map((thread) =>
            thread.id === threadId ? { ...thread, messages: response || [], isExpanded: true } : thread
          )
        );
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingThreadId(null);
    }
  };

  // Debounced API call effect - This handles all filter changes with debouncing
  useEffect(() => {
    // Clear any existing timeout
    if (apiCallTimeoutRef.current) {
      clearTimeout(apiCallTimeoutRef.current);
    }

    // Only set up debounced call if modal is open
    if (isChatHistoryModalOpen) {
      apiCallTimeoutRef.current = setTimeout(() => {
        let selectedUserObj = null;
        if (users.length > 0) {
          selectedUserObj = users.find(user => user.user_name === selectedUser);
        }

        const request: ChatHistoryRequest = {
          app_id: assistant.app_id,
          user_id: selectedUserObj ? selectedUserObj.user_id : "",
          date_range: {
            start_date: selectedDateRange[0] ? selectedDateRange[0].toISOString() : null,
            end_date: selectedDateRange[1] ? selectedDateRange[1].toISOString() : null,
          },
          topic_search: topicSearchTerm,
          pagination: {
            pageSize: pagination.pageSize,
            skip: pagination.skip,
            searchTerm: pagination.searchTerm,
            sortBy: pagination.sortBy,
            sortOrder: pagination.sortOrder,
          },
        };

        fetchHistory(request);
      }, 300); // 300ms debounce - good balance between responsiveness and preventing multiple calls
    }

    // Cleanup function
    return () => {
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }
    };
  }, [
    isChatHistoryModalOpen, 
    pagination, 
    selectedUser, 
    topicSearchTerm, 
    selectedDateRange, 
    users, 
    assistant.app_id
  ]);

  // Fetch users (separate effect since this doesn't need debouncing)
  useEffect(() => {
    const fetchUserList = async () => {
      try {
        const fetchedUsers = await fetchUsers(assistant?.app_id);
        setUsers(fetchedUsers);
        hasFetchedUser.current = true;
      } catch (error) {
        console.error("Error fetching users:", error);
        hasFetchedUser.current = false;
      }
    };

    if (isChatHistoryModalOpen && String(assistant?.is_authentication_enabled) === 'true' && !hasFetchedUser.current) {
      fetchUserList();
    }
  }, [isChatHistoryModalOpen, assistant?.app_id, assistant?.is_authentication_enabled]);

  // Reset user fetch flag when modal closes
  useEffect(() => {
    if (!isChatHistoryModalOpen) {
      hasFetchedUser.current = false;
    }
  }, [isChatHistoryModalOpen]);

  // Modal helper functions
  const showCustomModal = (title: string, message: string, type: "success" | "error" | "loading" | "confirmation", onConfirm?: () => void) => {
    setCustomModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm,
    });
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    let selectedUserObj = null;
    if (users.length > 0)
      selectedUserObj = users.find(user => user.user_name === selectedUser);
    
    const request: ChatHistoryRequest = {
      app_id: assistant?.app_id,
      user_id: selectedUserObj ? selectedUserObj.user_id : "",
      date_range: {
        start_date: selectedDateRange[0] ? selectedDateRange[0].toISOString() : null,
        end_date: selectedDateRange[1] ? selectedDateRange[1].toISOString() : null,
      },
      topic_search: topicSearchTerm,
      pagination: {
        pageSize: pagination.pageSize,
        skip: pagination.skip,
        searchTerm: pagination.searchTerm,
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder,
      },
    };

    const response = await exportChat(request);

    if (response && typeof response === 'string') {
      // ✅ Simply download the CSV as-is, no manipulation needed
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chat_history.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setIsDownloading(false);
    } else {
      console.error('No valid CSV data received for download');
    }
  };

  const handleDeleteThread = (threadId: string) => {
    showConfirmationModal("Do you want to delete this chat?", async () => {
      setCustomModal((prev) => ({
        ...prev,
        isOpen: false,
      }));

      setCustomModal({
        isOpen: true,
        title: "Deleting...",
        message: "Please wait while we deleting the chat.",
        type: "loading",
      });

      try {
        const responseData = await deleteChat(assistant?.app_id, threadId);

        if (responseData.message?.includes("deleted successfully")) {
          const selectedUserObj = users.find(user => user.user_name === selectedUser);

          const request: ChatHistoryRequest = {
            app_id: assistant?.app_id,
            user_id: selectedUserObj ? selectedUserObj.user_id : "",
            date_range: {
              start_date: selectedDateRange[0] ? selectedDateRange[0].toISOString() : null,
              end_date: selectedDateRange[1] ? selectedDateRange[1].toISOString() : null,
            },
            topic_search: topicSearchTerm,
            pagination: {
              pageSize: pagination.pageSize,
              skip: pagination.skip,
              searchTerm: pagination.searchTerm,
              sortBy: pagination.sortBy,
              sortOrder: pagination.sortOrder,
            },
          };

          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: "Chat deleted successfully.",
            type: "success",
          });
          await fetchHistory(request);
        } else {
          throw new Error("Unexpected response");
        }
      } catch (error) {
        setCustomModal({
          isOpen: true,
          title: "Error!",
          message: "There was an issue deleting chat.",
          type: "error",
        });
      }
    });
  };

  const filteredAndSortedThreads = (Array.isArray(chatThreads) ? chatThreads : []);

  return (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60 overflow-auto ${
        isChatHistoryModalOpen ? 'block' : 'hidden'
      }`}
      onClick={() => setIsChatHistoryModalOpen(false)}
    >
      <div
        className="bg-white w-[90%] h-[80%] shadow-lg rounded-lg flex flex-col overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CustomModal
          isOpen={customModal.isOpen}
          onClose={() => setCustomModal({ ...customModal, isOpen: false })}
          onConfirm={customModal.onConfirm}
          title={customModal.title}
          message={customModal.message}
          type={customModal.type}
        />

        {/* Header */}
        <div className="p-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <FontAwesomeIcon icon={faMessage} className="h-5 w-5" />
              <h2 className="text-xl font-semibold text-gray-800">Chat History</h2>
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-500 rounded-full text-black font-semibold hover:bg-black hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span className="animate-spin border-t-2 border-black rounded-full w-4 h-4"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" /> Download
                  </>
                )}
              </button>
              <button
                onClick={() => setIsChatHistoryModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <hr className="mt-4" />

          {/* Filters Component */}
          <ChatHistoryFilters
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            showDatePicker={showDatePicker}
            setShowDatePicker={setShowDatePicker}
            datePickerRef={datePickerRef}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            users={users}
            topicSearchTerm={topicSearchTerm}
            setTopicSearchTerm={setTopicSearchTerm}
            selectedSortBy={selectedSortBy}
            setSelectedSortBy={setSelectedSortBy}
            selectedSortOrder={selectedSortOrder}
            setSelectedSortOrder={setSelectedSortOrder}
            assistant={assistant}
            fetchHistory={fetchHistory}
            pagination={pagination}
          />
        </div>

        {/* Table Component */}
        <div className="overflow-auto mt-2 px-4">
          {/* AI Summary Component */}
          <AISummary
            chatThreads={filteredAndSortedThreads}
            isLoading={isLoading}
            assistant={assistant}
          />

          <div className="mt-4">
            <ChatHistoryTable
            isLoading={isLoading}
            chatThreads={filteredAndSortedThreads}
            loadingThreadId={loadingThreadId}
            setChatThreads={setChatThreads}
            getMessages={getMessages}
            handleDeleteThread={handleDeleteThread}
            safeAiIconSrc={safeAiIconSrc}
            assistant={assistant}
          />
          </div>

          {/* Pagination Component */}
          <ChatHistoryPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pagination={pagination}
            setPagination={setPagination}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;